import React, {useState, useContext, useEffect} from "react";
import useStyle from '../../hooks/useStyle';
import { AuthContext } from "../../contexts/Authentication/AuthStateProvider";
import GenericModal from "../Modals/GenericModal";
import { ParaRichTextView } from "./RichTextView";
import { ProfileImage } from "./Image";

const NotificationPopup = (props) => {
  useStyle('dashboard');      

  return(
    <div className="col-xs-12">
        <div className="card card-style">
          <div className={props.cardCss || ''}>
            <div className="success d-flex">
              <div>
                {props.imageIco ?
                  <ProfileImage src={props.imageIco} />
                  :
                  <h3><i className={props.iconClass || 'fa fa-check bg-green1-dark success-ico'}/></h3>
                }
              </div>
              <div className="success-txt">
                <div className="f18">{props.title || props.titleLabel || 'Success'}</div>
                <ParaRichTextView className="pre-wrap m-t-5 f15" html={props.message}/>
                {props.date?<span className="text-gray">{props.date}</span>:null}
              </div>
            </div>
            <div className="pull-right m-r-20">
              {props.onSuccess &&
                <span onClick={()=>props.onSuccess()} className="button button-s button-round-small bg-submit">
                  {props.successLabel || 'OK'}
                </span>
              }
              {props.onCancel && 
                <span onClick={()=>props.onCancel()} className="button button-s button-round-small bg-cancel m-l-20">
                  {props.cancelLabel || 'Cancel'}
                </span>
              }
            </div>  
          </div>
        </div>
    </div>
  )
}

const ConfirmAlert = (props) => {
  useStyle('dashboard');      

  return(
    <div className="col-xs-12 bg-white">
      <div className="p-10">
        <div className="success d-flex">
          <div className="success-txt">
            <ParaRichTextView className="font-16" html={props.message}></ParaRichTextView>
          </div>
        </div>
        <div className="col-xs-12 d-flex p-10">
          {props.options.map((o, i)=>
            <span key={i} 
              onClick={e=>props.onSelect(o.sid)} 
              className={`p-5 m-l-15 m-b-5 font-16 text-center w-50p white ${o.color}`}
              htmlFor={o.html_for}>
                {o.label}
            </span>
          )}
        </div>  
      </div>
    </div>
  )
}

const ScreenOrientationAlert = ({alert}) =>{
  useStyle('dashboard');   
  const {state: {screen}} = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(screen.xs);

  const onSuccess = () => {
    localStorage.ct_screen_rotate_warning_dismiss = true;
    setIsOpen(false);
  }

  const Alert = () =>{
    return(
      <div className="col-xs-12">
        <div className="card card-style">
          <div className="success">
            <div>
              <h3><i className="fas fa-info-circle bg-green1-dark success-ico"/></h3>
            </div>
            <div className="success-txt">
              <div className="ore-alert">{alert}</div>
              <div>
                <img src="./images/orientation-200.png" className="w-100"/>
              </div>
            </div>
          </div>
          <div>
            <span onClick={()=>onSuccess()} className="success-btn w-75">OK</span>
          </div>  
        </div>
      </div>
    )
  }

  if(!localStorage.ct_screen_rotate_warning_dismiss && isOpen){
    return(
      <GenericModal component={Alert} isOpen={isOpen} toggleModal={onSuccess}/>
    )
  }  
  
  return null  
}

const AlertSM = ({className="bg-red1-dark", text, isOpen, toggleModal, posStyle={right: 0, top: '150px'}, autoClose=false}) =>{

  useEffect(()=>{
    if(isOpen && autoClose){
      setTimeout(function() {
        toggleModal()
      }, 10000);
    }
  }, [isOpen])

  if(isOpen){
    return(
      <span className={className+" p-15 pos-fix"} style={posStyle}>
        {text}
      </span>
    )
  }

  return null
}

const HelpMatchAlert = ({ requestTitle, deadlineTime, deadlineDate, onReview, onRemindLater }) => {
  return (
    <div className="bg-white brd-10 shadow-small p-20">
      <div className="fw700 font-16 color-black m-b-15">You may be able to help</div>

      <div className="bg-shade-green brd-10 p-15 m-b-15 flex gap-10">
        <i className="fas fa-wand-magic-sparkles text-green font-14 m-t-2"></i>
        <div className="font-13 text-green fw500 lh-19">
          Your expertise and research areas appear to match the subject matter of a help request.
          <div className="m-t-5">Request: "{requestTitle}".</div>
        </div>
      </div>

      <div className="flex aligncenter gap-10 font-12 text-lite-gray m-b-10">
        <i className="fas fa-clock font-11"></i>
        <span>
          Response deadline: <span className="fw700 black">{deadlineTime}</span> · Due {deadlineDate}
        </span>
      </div>

      <div className="font-11 text-lite-gray m-b-20">
        Reviewing the request does not share your identity. You decide on the next screen whether to connect.
      </div>

      <div className="flex justspacebetween aligncenter br-top-grey p-t-15">
        <span className="font-13 fw500 black pointer" onClick={onRemindLater}>Remind me later</span>
        <button
          className="btn-primary bg-green1-dark text-white brd-5 font-13 fw500 p-8-15 flex aligncenter gap-5 no-border"
          onClick={onReview}>
          Review the request <i className="fas fa-arrow-right font-11"></i>
        </button>
      </div>
    </div>
  )
}

const ConnectDecisionModal = ({ onConfirm, onDecline }) => {
  const [wantsConnect, setWantsConnect] = useState(null);
  const [response, setResponse] = useState('');
  const [agreed, setAgreed] = useState(false);

  const handleConfirm = () => {
    if (wantsConnect === false) {
      onDecline && onDecline();
      return;
    }
    onConfirm && onConfirm({ response, agreed });
  };

  return (
    <div className="bg-white brd-10 shadow-small p-20">
      <div className="fw700 font-16 color-black m-b-5">Do you wish to be connected?</div>
      <div className="font-12 text-lite-gray lh-19 m-b-15">
        If you choose to connect, the identity of <span className="fw700 black">both</span> users will be disclosed to each other so you can exchange information.
      </div>

      <div className="flex gap-10 m-b-20">
        <span
          onClick={() => setWantsConnect(true)}
          className={`flex aligncenter justifycenter gap-10 brd-8 p-10 font-13 fw500 pointer w-100p ${wantsConnect === true ? 'bg-green1-dark text-white no-border' : 'br-grey black'}`}>
          <i className="fas fa-user-plus font-12"></i> Yes, connect us
        </span>
        <span
          onClick={() => setWantsConnect(false)}
          className={`flex aligncenter justifycenter gap-10 brd-8 p-10 font-13 fw500 pointer w-100p ${wantsConnect === false ? 'br-grey-2 black' : 'br-grey black'}`}>
          <i className="fas fa-times font-12"></i> No, not this time
        </span>
      </div>

      <div className="font-12 fw500 black m-b-5">
        A short response to the requester <span className="font-11 text-lite-gray fw400">(optional, shown on their dashboard)</span>
      </div>
      <textarea
        className="form-input-gray form-text-area w-100p p-10 m-b-15"
        placeholder="Write a short response..."
        value={response}
        onChange={(e) => setResponse(e.target.value)} />

      <div className="flex gap-10 m-b-20">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="m-t-2"
        />
        <span className="font-12 text-lite-gray lh-19">
          I agree to the <span className="text-blue pointer">terms and conditions</span> of the matching application, and I understand my identity will be shared with the other party.
        </span>
      </div>

      <div className="flex justifyflexend">
        <button
          className="btn-primary bg-green1-dark text-white brd-5 font-13 fw500 p-8-15 no-border pointer"
          disabled={!agreed || wantsConnect === null}
          onClick={handleConfirm}>
          Confirm and connect
        </button>
      </div>
    </div>
  )
}

export { NotificationPopup, ConfirmAlert, ScreenOrientationAlert, AlertSM, HelpMatchAlert, ConnectDecisionModal };